import { useState, useEffect, useCallback } from 'react'
import { Row, Col } from 'antd'
import moment from 'moment'
import _ from 'lodash'
import { ResponsiveContainer, ComposedChart, Bar, XAxis, CartesianGrid, Legend, LabelList } from 'recharts'
import { Loader } from '@vzmi/shp_shared_compo'
import { httpHandler } from '@vzmi/component_ec_nodejs_util/dist/lib/FetchDataUtil'
import { numeralFormat } from '@vzmi/component_ec_nodejs_util/dist/lib/renderUtil'
import XAxisTick from '../../../Common/NewDashboard/Chart/XAxisTick/XAxisTick'
import ComposeChartModule from '../../../Common/NewDashboard/Chart/BaseChartModule/ComposeChart/ComposeChart'
import { StatsCard } from '../StatsCard/StatsCard'
import { Error } from '../../../Common/StatePage'
import Information from '../../../Common/Information'
import safeAwait from '../../../../../lib/safeAwait'
import errorHelper from '../../../../../lib/errorHelper'
import { getUIFormatDate } from '../../../../lib/MomentHelper'
import { EMPTY_VALUE } from '../../../../constants/globalTypes'
import styles from './Overview.css'

export const FIELD = {
  TYPENAME: 'typeName',
  MEMBERQUANTITY: 'memberQuantity',
  COMPUTED_PERCENT: 'computedPercent'
}

/**
 * transfrom data to chart data
 * add fill by type 104 to 105 for '#883BC6', and 107 to 112 for '#6a96f3'
 * then compute percent of each fill type
 * API type reference: https://git.ouryahoo.com/TW-Shopping-BackOffice/component_shopping_spa_doc/blob/e3d070ac34d44aa0d884d751fb9cf9dea5dea9a3/public/api/appendix/mappings.mdx
 * @param {Array} data fetched from api
 * @returns {Array} transformed data
 **/
const transformToChartData = data => {
  const sumOfType = {}
  return data.reduce((acc, cur) => {
    const { type, memberQuantity } = cur
    const fill = type >= 104 && type <= 105 ? '#883BC6' : '#6a96f3'
    sumOfType[fill] = (sumOfType[fill] || 0) + memberQuantity
    acc.push({
      ...cur,
      fill,
      type,
      memberQuantity
    })
    return acc
  }, []).map(item => {
    const { memberQuantity, fill } = item
    const computedPercent = Math.round(memberQuantity / sumOfType[fill] * 100)
    return {
      ...item,
      computedPercent
    }
  })
}

const Overview = ({ fetchMemberAnalyses }) => {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [fetchError, setFetchError] = useState(null)

  const search = useCallback(async () => {
    setIsLoading(true)
    const [reqError, payload = {}] = await safeAwait(
      fetchMemberAnalyses({ group: 'overview' }).then(httpHandler)
    )
    setIsLoading(false)
    const errorMessage = errorHelper.parseErrorMessage(reqError)
    setFetchError(errorMessage)
    if (reqError) {
      return
    }

    const { analyses = [] } = payload
    setData(analyses)
  }, [fetchMemberAnalyses])

  useEffect(() => {
    search()
  }, [fetchMemberAnalyses, search])

  const [periodStartDate, periodEndDate] = [-1, 0].map(year => {
    const reportDate = _.get(data, '0.reportDate')
    if (_.isEmpty(reportDate)) {
      return EMPTY_VALUE
    }
    if (year === 0) {
      return getUIFormatDate(moment(reportDate))
    }
    return getUIFormatDate(moment(reportDate).add('years', year).add('days', 1))
  })

  return (
    <Loader loaded={!isLoading}>
      {
        fetchError
          ? (
            <Error
              onClick={search}
              iconType='update'
              text='系統忙碌中，請稍後再試'
            />
          )
          : (
            <div className={styles.overview}>
              <MemberOverviewDescription />
              <Information description={`資料統計區間：${periodStartDate} ~ ${periodEndDate}（每日中午更新）`} />
              <div className={styles.barChart}>
                <p>買家輪廓分佈</p>
                {/* use data type between 104~112 except 106,112 (Unknow gender & age) */}
                <Chart data={data.filter(item => item.type >= 104 && item.type <= 111 && item.type !== 106)} />
              </div>
              {/* use data type between 101~103 for cards */}
              <CardList data={data.filter(item => item.type >= 101 && item.type <= 103)} />
            </div>
          )
      }
    </Loader>
  )
}

export const MemberOverviewDescription = () => (
  <>
    <Information description='會員：一年內有購買紀錄之買家' />
    <Information description='新客：90天內首購'/>
    <Information description='回客：是會員且30天內再次購買'/>
  </>
)
class Chart extends ComposeChartModule {
  constructor () {
    super()
    this.state = {}
  }

  render () {
    const { data } = this.props
    const chartData = transformToChartData(data)
    return (
      <div className={styles.chartBlock}>
        <ResponsiveContainer
          width='100%'
          height='100%'
          minHeight={'250px'}
        >
          <ComposedChart
            ref={this.initChart}
            data={chartData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5
            }}
          >
            <XAxis
              tickLine={false}
              dataKey={FIELD.TYPENAME}
              tick={<CustomizeXAxisTick />}
              interval={0}
            />
            <CartesianGrid
              vertical={false}
              stroke='#f5f5f5'
            />
            <Legend
              payload={[{
                type: 'square',
                value: '性別',
                color: '#883BC6'
              }, {
                type: 'square',
                value: '年齡',
                color: '#6a96f3'
              }]}
            />
            <Bar
              yAxisId='left'
              dataKey={FIELD.COMPUTED_PERCENT}
              legendType='square'
              barSize={this.state.barSize}
            >
              <LabelList
                dataKey={FIELD.COMPUTED_PERCENT}
                content={renderCustomizedLabel}
              />
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    )
  }
}

const CustomizeXAxisTick = ({ x, y, payload }) => (
  <XAxisTick svgProps={{ x, y }}>
    <tspan
      x={0}
      dy={12}
    >
      {payload.value}
    </tspan>
  </XAxisTick>
)

const renderCustomizedLabel = (props) => {
  const { x, y, width, value } = props
  const radius = 7

  return (
    <g>
      <text
        x={x + width / 2}
        y={y - radius}
        fill="#333"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {value}
        %
      </text>
    </g>
  )
}

const CardList = ({ data }) => {
  const listItems =
    data.map(
      (item, index) => {
        const { typeName, memberQuantity } = item
        const formatedVal = numeralFormat(memberQuantity)
        return (
          <Col
            key={index}
          >
            <StatsCard
              title={'title'}
              titleContent={typeName}
              content={formatedVal || EMPTY_VALUE}
            />
          </Col>
        )
      })

  return (
    <Row
      data-role='memStat-list'
      className={styles.cardList}
    >
      {listItems}
    </Row>
  )
}

export default Overview
